
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {Utils} from "../utils.js";

const defaultColors = {
	"landuse":   [0.5, 0.5, 0.5],
	"natural":   [0.0, 1.0, 0.0],
	"places":    [1.0, 0.0, 1.0],
	"points":    [0.0, 1.0, 1.0],
	"roads":     [1.0, 1.0, 0.0],
	"waterways": [0.0, 0.0, 1.0],
	"default":   [0.9, 0.6, 0.1],
};

function getColor(feature){
	let color = defaultColors[feature];

	if(!color){
		color = defaultColors["default"];
	}

	return color;
}

export class Geopackage{
	constructor(){
		this.path = null;
		this.node = null;
		this.zOffset = 0;
		this.format = null;
		this.data = null;
	}
};

export class GeoPackageLoader{

	constructor(){

	}

	static async loadUrl(url, params){

		await Promise.all([
			Utils.loadScript(`${Potree.scriptPath}/lazylibs/geopackage/geopackage.js`),
			Utils.loadScript(`${Potree.scriptPath}/lazylibs/sql.js/sql-wasm.js`),
		]);

		const result = await fetch(url);
		const buffer = await result.arrayBuffer();

		params = params || {};

		params.source = url;

		return GeoPackageLoader.loadBuffer(buffer, params);
	}

	static async loadBuffer(buffer, params){

		await Promise.all([
			Utils.loadScript(`${Potree.scriptPath}/lazylibs/geopackage/geopackage.js`),
			Utils.loadScript(`${Potree.scriptPath}/lazylibs/sql.js/sql-wasm.js`),
		]);

		params = params || {};

		const resolver = async (resolve, reject) => {
			try {

			let transform = params.transform;
			if(!transform){
				transform = {forward: (arg) => arg};
			}

			const wasmPath = `${Potree.scriptPath}/lazylibs/sql.js/sql-wasm.wasm`;
			const SQL = await initSqlJs({ locateFile: filename => wasmPath});

			let u8 = new Uint8Array(buffer);

			// Map of sanitized → original table names, populated below if we had to
			// rename tables for the geopackage.js library's sake. Used after load to
			// restore the original names in the scene graph.
			const sanitizedToOriginal = new Map();

			// Pre-fix pass: (1) rename feature tables whose names contain characters
			// outside [A-Za-z0-9_], because the bundled geopackage.js library
			// interpolates table names unquoted into SQL (e.g. the rtree trigger
			// setup at line ~8178 and rtree-rebuild at line ~8245), which fails for
			// tables like "MapCad3D_Paved Road" or "Cataster — flurstcke"; (2) fix
			// geometry columns typed as BLOB (what GDAL/fiona emit) to match the
			// actual geometry type name, because the library compares string types.
			try {
				const fixDb = new SQL.Database(u8);
				let modified = false;

				const contents = fixDb.exec("SELECT table_name FROM gpkg_contents WHERE data_type='features'");
				const origToSafe = new Map();
				if (contents.length > 0) {
					const taken = new Set(contents[0].values.map(r => r[0]));
					for (const [origName] of contents[0].values) {
						if (!/[^A-Za-z0-9_]/.test(origName)) continue;
						const base = origName.replace(/[^A-Za-z0-9_]/g, '_');
						let safe = base;
						let i = 1;
						while (taken.has(safe) || [...origToSafe.values()].includes(safe)) {
							safe = `${base}_${i++}`;
						}
						origToSafe.set(origName, safe);
					}
				}
				if (origToSafe.size > 0) {
					// SQLite otherwise recompiles trigger bodies during ALTER TABLE RENAME
					// and during DDL that touches rtree artefacts; some sql.js builds
					// don't parse "CREATE TRIGGER ... rtree_..." cleanly in that path.
					try { fixDb.run("PRAGMA legacy_alter_table = 1"); } catch (e) { /* ignore */ }

					for (const [orig, safe] of origToSafe.entries()) {
						// Patch sqlite_master directly to rename the rtree virtual-table
						// artefacts and drop the triggers that reference them. Avoids
						// DDL that would re-parse the trigger bodies.
						try {
							fixDb.run("PRAGMA writable_schema = 1");
							fixDb.run(
								"DELETE FROM sqlite_master WHERE type='trigger' AND tbl_name=?",
								[orig]
							);
							fixDb.run(
								"DELETE FROM sqlite_master WHERE name LIKE 'rtree_' || ? || '_%'",
								[orig]
							);
							fixDb.run("PRAGMA writable_schema = 0");
						} catch (e) { /* ignore */ }

						try {
							fixDb.run(
								"DELETE FROM gpkg_extensions WHERE table_name=? AND extension_name='gpkg_rtree_index'",
								[orig]
							);
						} catch (e) { /* gpkg_extensions optional */ }

						fixDb.run(`ALTER TABLE "${orig}" RENAME TO "${safe}"`);
						fixDb.run("UPDATE gpkg_contents SET table_name=? WHERE table_name=?", [safe, orig]);
						fixDb.run("UPDATE gpkg_geometry_columns SET table_name=? WHERE table_name=?", [safe, orig]);
						try {
							fixDb.run("UPDATE gpkg_extensions SET table_name=? WHERE table_name=?", [safe, orig]);
						} catch (e) { /* gpkg_extensions optional */ }

						sanitizedToOriginal.set(safe, orig);
						modified = true;
					}
				}

				const geomCols = fixDb.exec("SELECT table_name, column_name, geometry_type_name FROM gpkg_geometry_columns");
				if (geomCols.length > 0) {
					for (const row of geomCols[0].values) {
						const [tableName, colName, geomType] = row;
						const info = fixDb.exec(`PRAGMA table_info("${tableName}")`);
						if (info.length > 0) {
							const colIdx = info[0].columns.indexOf('name');
							const typeIdx = info[0].columns.indexOf('type');
							for (const col of info[0].values) {
								if (col[colIdx] === colName && col[typeIdx] !== geomType) {
									// SQLite doesn't support ALTER COLUMN type, so recreate the table
									const createSql = fixDb.exec(`SELECT sql FROM sqlite_master WHERE type='table' AND name='${tableName}'`);
									if (createSql.length > 0) {
										const origSql = createSql[0].values[0][0];
										const fixedSql = origSql.replace(
											new RegExp(`(?:"${colName}"|\\b${colName})\\s+BLOB\\b`, 'i'),
											`${colName} ${geomType}`
										);
										if (fixedSql !== origSql) {
											fixDb.run(`ALTER TABLE "${tableName}" RENAME TO "_${tableName}_backup"`);
											fixDb.run(fixedSql);
											fixDb.run(`INSERT INTO "${tableName}" SELECT * FROM "_${tableName}_backup"`);
											fixDb.run(`DROP TABLE "_${tableName}_backup"`);
											modified = true;
										}
									}
								}
							}
						}
					}
				}

				if (modified) {
					u8 = new Uint8Array(fixDb.export());
				}
				fixDb.close();
			} catch (e) {
				console.warn('GeoPackageLoader: preparation fix skipped:', e.message);
			}

			const data = await geopackage.open(u8);

			const geopackageNode = new THREE.Object3D();
			geopackageNode.name = params.source;
			geopackageNode.potree = {
				source: params.source,
			};

			const geo = new Geopackage();
			geo.path = params.source;
			geo.node = geopackageNode;

			const zOffset = params.zOffset ?? 0;
			geo.zOffset = zOffset;
			geopackageNode.position.z = zOffset;

			const tables = data.getTables();

			// Build spatial index for tables that lack one, so
			// queryForGeoJSONFeaturesInTable can work.
			for (const table of tables.features) {
				try {
					await data.indexFeatureTable(table);
				} catch (err) {
					console.warn(`GeoPackageLoader: indexing failed for table "${table}":`, err.message);
				}
			}

			for(const table of tables.features){
				try {
					const dao = data.getFeatureDao(table);

					let boundingBox = dao.getBoundingBox();
					boundingBox = boundingBox.projectBoundingBox(dao.projection, 'EPSG:4326');
					// Use skipVerification=true because the library's verifyFeature
					// doesn't handle Multi* geometry types (MultiPolygon, etc.),
					// causing them to be silently dropped.
					const geoJsonIter = dao.queryForGeoJSONIndexedFeaturesWithBoundingBox(boundingBox, true);
					const geoJson = [];
					for (const feature of geoJsonIter) {
						geoJson.push(feature);
					}

					const matLine = new LineMaterial( {
						color: new THREE.Color().setRGB(...getColor(table)),
						linewidth: 2,
						resolution:  new THREE.Vector2(1000, 1000),
						dashed: false
					} );

					const node = new THREE.Object3D();
					node.name = sanitizedToOriginal.get(table) || table;
					geo.node.add(node);

					for(const [index, feature] of Object.entries(geoJson)){
						const featureNode = GeoPackageLoader.featureToSceneNode(feature, matLine, dao.projection, transform);
						if (featureNode) node.add(featureNode);
					}
				} catch (err) {
					console.warn(`GeoPackageLoader: failed to load table "${table}":`, err.message);
				}
			}

			resolve(geo);
			} catch (err) {
				reject(err);
			}
		}

		return new Promise(resolver);
	}

	static featureToSceneNode(feature, matLine, geopackageProjection, transform){
		let geometry = feature.geometry;

		let color = new THREE.Color(1, 1, 1);

		// GeoJSON from queryForGeoJSONFeaturesInTable is already in WGS84 (EPSG:4326),
		// so we only need transform.forward (WGS84 → point cloud CRS).
		const project = (coords) => {
			return transform.forward(coords);
		};

		if(feature.geometry.type === "Point"){
			let sg = new THREE.SphereGeometry(1, 18, 18);
			let sm = new THREE.MeshNormalMaterial();
			let s = new THREE.Mesh(sg, sm);

			let [long, lat] = geometry.coordinates;
			let pos = project([long, lat]);

			s.position.set(...pos, 0);

			s.scale.set(10, 10, 10);

			return s;
		}else if(geometry.type === "LineString"){
			let coordinates = [];

			let min = new THREE.Vector3(Infinity, Infinity, Infinity);
			for(let i = 0; i < geometry.coordinates.length; i++){
				let [long, lat] = geometry.coordinates[i];
				let pos = project([long, lat]);

				min.x = Math.min(min.x, pos[0]);
				min.y = Math.min(min.y, pos[1]);
				min.z = Math.min(min.z, 0);

				coordinates.push(...pos, 0);
				if(i > 0 && i < geometry.coordinates.length - 1){
					coordinates.push(...pos, 0);
				}
			}

			for(let i = 0; i < coordinates.length; i += 3){
				coordinates[i+0] -= min.x;
				coordinates[i+1] -= min.y;
				coordinates[i+2] -= min.z;
			}

			const lineGeometry = new LineGeometry();
			lineGeometry.setPositions( coordinates );

			const line = new Line2( lineGeometry, matLine );
			line.computeLineDistances();
			line.scale.set( 1, 1, 1 );
			line.position.copy(min);

			return line;
		}else if(geometry.type === "Polygon"){
			const group = new THREE.Object3D();
			for(const ring of geometry.coordinates){
				const sub = GeoPackageLoader.featureToSceneNode(
					{geometry: {type: "LineString", coordinates: ring}},
					matLine, geopackageProjection, transform
				);
				if(sub) group.add(sub);
			}
			return group;
		}else if(geometry.type === "MultiPoint"){
			const group = new THREE.Object3D();
			for(const coords of geometry.coordinates){
				const sub = GeoPackageLoader.featureToSceneNode({geometry: {type: "Point", coordinates: coords}}, matLine, geopackageProjection, transform);
				if(sub) group.add(sub);
			}
			return group;
		}else if(geometry.type === "MultiLineString"){
			const group = new THREE.Object3D();
			for(const coords of geometry.coordinates){
				const sub = GeoPackageLoader.featureToSceneNode({geometry: {type: "LineString", coordinates: coords}}, matLine, geopackageProjection, transform);
				if(sub) group.add(sub);
			}
			return group;
		}else if(geometry.type === "MultiPolygon"){
			const group = new THREE.Object3D();
			for(const polyCoords of geometry.coordinates){
				const sub = GeoPackageLoader.featureToSceneNode({geometry: {type: "Polygon", coordinates: polyCoords}}, matLine, geopackageProjection, transform);
				if(sub) group.add(sub);
			}
			return group;
		}else{
			console.log("unhandled feature: ", feature);
		}
	}

};
